import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";

import { coordinatedSilentRenew } from "./crossTabRenewCoordination";
import { renewWithRetry } from "./renewWithRetry";
import { isSessionEndRenewError } from "./shouldRetrySilentRenew";

const CLIENT_ID = process.env.NEXT_PUBLIC_CLIENT_ID;

// How many seconds before access-token expiry we attempt a silent renewal.
// Shared between the UserManager config and every re-attempt site (the
// on-refocus/online/pageshow handlers in AuthProvider, the stay-signed-in
// freshness check in useIdleLogout) so they all use the same window.
export const ACCESS_TOKEN_EXPIRING_NOTIFICATION_SECONDS = 60;

// Upper ceiling on every OIDC HTTP call (metadata discovery, jwks, and — the
// CUSTOMER-UI-3Z Mode A boot wedge — the signin-callback code-exchange POST),
// converting an indefinite hang into a bounded rejection. Deliberately ABOVE
// the ~15s boot-splash watchdog confirm window so it never aborts a
// slow-but-succeeding token exchange on a poor mobile link; do not lower
// below 15. A rejection routes into existing recovery only: resolveAuthAction
// strips the callback params and re-redirects to Keycloak, self-limited by the
// signin-redirect budget into the parked Reload-app state. Also threaded
// through the signinSilent wrapper below because the UserManager setting does
// NOT reach the refresh-token grant (oidc-client-ts takes it as a per-call
// arg there, which react-oidc-context leaves undefined).
export const OIDC_REQUEST_TIMEOUT_SECONDS = 20;

/**
 * OAuth scope requested at sign-in.
 *
 * We do NOT request `offline_access` by default. Our Keycloak customer clients
 * are not assigned that client scope, and — contrary to a common assumption —
 * Keycloak does NOT silently omit an unassigned requested scope: it rejects the
 * `/authorize` request with `invalid_scope`. Because `resolveAuthAction` treats
 * a callback error as "redirect to log in", requesting an ungranted
 * `offline_access` produced a sign-in redirect LOOP that parked every user on
 * the splash screen and, via the repeated full-page reloads, flooded Sentry's
 * ingest into HTTP 429 (UAT, 2026-07-06). See `.claude/docs/incident-log.md`
 * and memory `oidc-no-offline-access-scope.md`.
 *
 * Consequence of not requesting it: the refresh token's lifetime stays capped
 * by Keycloak's SSO Session Idle (~15min) — the original CUSTOMER-UI-56/57
 * "bounced out during a long pause" constraint. That is accepted: the other
 * renewal-recovery layers on this branch (in-place silent renew before any
 * redirect, baseQuery 401 renew-and-retry, refocus/online/pageshow re-renew,
 * renewWithRetry backoff, the bounded mid-window retry, local-teardown logout)
 * do NOT depend on `offline_access` and remain the mitigation; the inactivity
 * logout is unaffected.
 *
 * Opt in ONLY once the client actually has the `offline_access` optional client
 * scope assigned in Keycloak (build-time inlined — needs a redeploy):
 * NEXT_PUBLIC_AUTH_OFFLINE_SCOPE_ENABLED=true. If it is enabled against a realm
 * that still grants only a subset, AuthProvider reports
 * `auth-offline-scope-not-granted`.
 */
const OFFLINE_SCOPE_ENABLED =
  process.env.NEXT_PUBLIC_AUTH_OFFLINE_SCOPE_ENABLED === "true";
export const OIDC_SCOPE_INCLUDES_OFFLINE_ACCESS = OFFLINE_SCOPE_ENABLED;
export const OIDC_SCOPE = OFFLINE_SCOPE_ENABLED
  ? "openid offline_access"
  : "openid";

// Set when a silent renewal is skipped because the tab is backgrounded (see
// the signinSilent wrapper). A subsequent accessTokenExpired is then the
// ACCEPTED consequence of that skip and is reported as a breadcrumb, not an
// alert. Cleared once a foreground renewal is attempted. Module-scoped
// because the single UserManager below and the auth-event handlers
// (AuthProvider's AuthProtection) share one app-wide instance.
let renewalSkippedWhileHidden = false;

export const wasRenewalSkippedWhileHidden = () => renewalSkippedWhileHidden;
export const clearRenewalSkippedWhileHidden = () => {
  renewalSkippedWhileHidden = false;
};

/**
 * The message of the most recent renewal failure that exhausted all recovery
 * (retries, cross-tab lock, storage recheck), cleared on the next success.
 * Exists because react-oidc-context's `signinSilent` SWALLOWS rejections
 * (dispatches ERROR internally, resolves null), so the last-chance expiry
 * report in AuthProvider otherwise loses the real Keycloak error_description
 * (every CUSTOMER-UI-57 event just said "renewal-skipped-or-null").
 */
let lastRenewalErrorMessage: string | undefined;

export const getLastRenewalErrorMessage = () => lastRenewalErrorMessage;

let userManager: UserManager | undefined;

/**
 * The single app-wide UserManager. A module singleton (not per-AuthProvider
 * state) for two reasons:
 *
 * 1. If AuthProvider remounts during Next.js Suspense transitions, a new
 *    UserManager would create duplicate SilentRenewService timers that
 *    compound into ~1s token polling (performance-patterns.md §5).
 * 2. Non-React code needs it too: `redux/services/baseQuery.ts` performs a
 *    coalesced silent renewal + one retry when a request 401s, which heals
 *    the "UI still up but token just lapsed" window without a bounce.
 *
 * `signinSilent` is wrapped with (a) a hidden-tab skip, (b) a concurrency
 * guard coalescing parallel callers into one token request (oidc-client-ts
 * v3.4.1 can fire accessTokenExpiring multiple times in quick succession),
 * (c) transient-failure retries with backoff (renewWithRetry), and (d) a
 * cross-tab Web Lock + stored-user adoption so multiple tabs can't race
 * Keycloak's refresh-token rotation into a spurious `invalid_grant` bounce
 * (crossTabRenewCoordination.ts).
 */
export function getOidcUserManager(): UserManager | undefined {
  if (typeof window === "undefined") return undefined;
  if (userManager) return userManager;

  // Use origin (not href) so the OIDC config is stable across route changes.
  const origin = window.location.origin;

  const um = new UserManager({
    authority: process.env.NEXT_PUBLIC_AUTH_URL,
    client_id: CLIENT_ID,
    redirect_uri: origin,
    post_logout_redirect_uri: origin,
    silent_redirect_uri: `${origin}/silent-renew.html`,
    scope: OIDC_SCOPE,
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds:
      ACCESS_TOKEN_EXPIRING_NOTIFICATION_SECONDS,
    requestTimeoutInSeconds: OIDC_REQUEST_TIMEOUT_SECONDS,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  });

  // A stored user is adoptable (instead of renewing ourselves) only when its
  // access token is non-expired AND DIFFERENT from the one this renewal
  // started with — the shared localStorage store changing under us is proof
  // another tab completed a renewal in the meantime. Deliberately NOT a
  // time-window check: a token can be time-valid yet server-rejected (the
  // baseQuery 401 path), and re-adopting our own token there would skip a
  // renewal that must happen.
  const getStoredUserRenewedByOtherTab = async (
    baseline: User | null
  ): Promise<User | null> => {
    const stored = await um.getUser();
    if (!stored || stored.expired) return null;
    if (baseline && stored.access_token === baseline.access_token) return null;
    return stored;
  };

  // Coalesce concurrent signinSilent calls into a single request.
  // If a refresh is already in flight, return the same promise.
  let pendingRenew: ReturnType<typeof um.signinSilent> | null = null;
  const originalSigninSilent = um.signinSilent.bind(um);
  um.signinSilent = (args) => {
    // Don't renew in a backgrounded tab. Browser timer throttling fires the
    // renewal late, and renewing against a Keycloak SSO session that
    // idle-expired while we were hidden just returns "Session not active" —
    // wasted network + Sentry noise (CUSTOMER-UI-56/57). Let the token lapse;
    // we re-attempt on visibilitychange when the tab returns to the foreground
    // (see AuthProvider), and a genuinely-dead session simply means the user
    // re-authenticates next time.
    if (
      typeof document !== "undefined" &&
      document.visibilityState === "hidden"
    ) {
      renewalSkippedWhileHidden = true;
      return Promise.resolve(null);
    }
    // Foreground attempt: clear the skip flag NOW, before awaiting — not only
    // on success. Otherwise a FAILED foreground renewal (e.g. on refocus)
    // would leave the flag set, and the resulting visible expiry would be
    // mis-reported as an expected background skip instead of the real
    // foreground failure it is. (A skip-then-expire-while-still-hidden is
    // still caught independently by the visibility check in
    // handleAccessTokenExpired.)
    renewalSkippedWhileHidden = false;
    if (pendingRenew) return pendingRenew;
    // The renewal is (a) serialized ACROSS tabs by a Web Lock with a
    // fresh-user storage recheck (another tab's renewal is adopted instead
    // of racing Keycloak's refresh-token rotation into invalid_grant —
    // crossTabRenewCoordination.ts), and (b) retried on transient failures
    // with backoff (renewWithRetry). The whole sequence is coalesced behind
    // the single `pendingRenew` promise so concurrent callers in THIS tab
    // still share one in-flight renewal.
    pendingRenew = (async () => {
      // The token we hold as renewal becomes necessary — the comparison
      // baseline for detecting another tab's renewal. Read BEFORE the lock
      // wait so time spent queueing can't blur what "changed under us" means.
      const baseline = await um.getUser();
      return coordinatedSilentRenew<User>({
        getFreshStoredUser: () => getStoredUserRenewedByOtherTab(baseline),
        // raiseEvent defaults to true → USER_LOADED reaches react-oidc-context
        // and re-arms the expiring/expired timers, exactly like a real renewal.
        adoptStoredUser: (user) => um.events.load(user),
        // silentRequestTimeoutInSeconds bounds the refresh-token POST (and
        // raises the rarely-taken iframe fallback's ceiling from its 10s
        // default to the same bound) — the constructor-level
        // requestTimeoutInSeconds does not reach this grant. Args spread
        // LAST so an explicit caller override still wins.
        renew: () =>
          renewWithRetry(() =>
            originalSigninSilent({
              silentRequestTimeoutInSeconds: OIDC_REQUEST_TIMEOUT_SECONDS,
              ...args,
            })
          ),
        isSessionEndError: isSessionEndRenewError,
      });
    })()
      .then(
        (user) => {
          lastRenewalErrorMessage = undefined;
          return user;
        },
        (error: unknown) => {
          lastRenewalErrorMessage =
            error instanceof Error ? error.message : String(error);
          throw error;
        }
      )
      .finally(() => {
        pendingRenew = null;
      });
    return pendingRenew;
  };

  userManager = um;
  return userManager;
}
